import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

function isNetworkError(err: unknown): boolean {
  if (!err) return false;
  const msg = typeof err === 'object' && err !== null && 'message' in err
    ? String((err as { message: unknown }).message)
    : String(err);
  return msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch failed');
}

export default function MaintenanceCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useAuth();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkMaintenance();

    const interval = setInterval(checkMaintenance, 30000);

    return () => clearInterval(interval);
  }, [user, role, location.pathname]);

  const checkMaintenance = async () => {
    try {
      if (location.pathname === '/maintenance') {
        setChecking(false);
        return;
      }

      // Vérifier aussi le force_logout_at dans system_config
      const { data: securityConfig } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'security')
        .maybeSingle();

      if (securityConfig?.config_value?.force_logout_at && role !== 'super_admin' && user) {
        const forceLogoutAt = new Date(securityConfig.config_value.force_logout_at);
        // Vérifier si la session a été créée avant le force logout
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionCreatedAt = sessionData?.session?.created_at;
        if (sessionCreatedAt && new Date(sessionCreatedAt) < forceLogoutAt) {
          console.log('Session antérieure au force logout, déconnexion...');
          await supabase.auth.signOut();
          navigate('/maintenance');
          setChecking(false);
          return;
        }
      }

      const { data, error } = await supabase
        .from('maintenance_mode')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        if (isNetworkError(error)) {
          console.warn('Maintenance check: Supabase injoignable, accès autorisé par défaut');
        } else {
          console.warn('Maintenance check: requête échouée, accès autorisé par défaut');
        }
        setChecking(false);
        return;
      }

      if (data) {
        if (role === 'super_admin') {
          console.log('Super admin détecté, accès autorisé pendant la maintenance');
          setChecking(false);
          return;
        }

        // Si end_time est null, la maintenance est illimitée - ne pas auto-désactiver
        if (data.end_time) {
          const endTime = new Date(data.end_time);
          const now = new Date();

          if (now > endTime) {
            await supabase
              .from('maintenance_mode')
              .update({ 
                is_active: false,
                end_time: null 
              })
              .eq('id', data.id);

            console.log('Maintenance terminée automatiquement');
            setChecking(false);
            return;
          }
        }

        // Déconnecter l'utilisateur non-admin ET rediriger vers la page maintenance
        console.log('Mode maintenance actif, déconnexion et redirection...');
        if (user) {
          await supabase.auth.signOut();
        }
        navigate('/maintenance');
      }

      setChecking(false);
    } catch (err) {
      if (isNetworkError(err)) {
        console.warn('Maintenance check: Supabase injoignable, accès autorisé par défaut');
      } else {
        console.warn('Maintenance check: erreur inattendue, accès autorisé par défaut');
      }
      setChecking(false);
    }
  };

  if (checking) {
    return null;
  }

  return null;
}